<template>
  <v-card class="pa-8 d-flex flex-column w-100" elevation="0" style="border-radius: 16px;">

    <div class="doughnutHeaderTitle">
      {{ title }}
    </div>

    <div v-if="!hasData" class="no-data-container">
      {{ emptyText }}
    </div>

    <div v-else class="chart-area d-flex">
      <div class="chart-container">
        <canvas ref="canvas"></canvas>
      </div>

      <div class="custom-legend ml-6">
        <div
            v-for="(label, index) in labels"
            :key="index"
            class="legend-item"
            @click="onLabelclick(index)"
        >
        <span
            class="legend-dot"
            :style="{ backgroundColor: resolvedColors[index] }"
        ></span>

          <span class="legend-text">{{ label }}</span>
          <span class="legend-value">{{ data[index] }}</span>
          <span class="legend-arrow"> ›</span>
        </div>
      </div>
    </div>

  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useTheme } from 'vuetify';
import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import {WidgetInfo} from "@/generated/classes/DashboardWidget.ts";

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

export default defineComponent({
  props: {
    id: {
      type: Number
    },
    title: {
      type: String,
    },
    labels: {
      type: Array as () => string[],
      required: true
    },
    data: {
      type: Array as () => number[],
      required: true
    },
    emptyText: {
      type: String,
      default: "Aucune donnée disponible"
    },
    chartOptions: {
      type: Object,
    }
  },

  data() {
    return {
      chart: null as Chart | null,
      resolvedColors: [] as string[],
    };
  },

  computed: {
    hasData() {
      return Array.isArray(this.data)
          && this.data.length > 0
          && this.data.some(v => v > 0)
    }
  },

  mounted() {
    const theme = useTheme();

    this.resolvedColors = [
      theme.current.value.colors.information30,
      theme.current.value.colors.information50,
      theme.current.value.colors.information70,
      theme.current.value.colors.information90
    ];

    const canvas = this.$refs.canvas as HTMLCanvasElement;

    this.chart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: this.labels,
        datasets: [
          {
            data: this.data,
            backgroundColor: this.resolvedColors,
            borderWidth: 0,
            borderColor: theme.current.value.colors.information70
          }
        ]
      },
      options: {
        ...this.chartOptions,
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const index = elements[0].index;
            this.onLabelclick(index);
          }
        },
        onHover: (event, elements) => {
          const canvas = event.native?.target;
          if (!canvas) return;

          canvas.style.cursor = elements.length > 0 ? "pointer" : "default";
        }
      }
    });
  },

  methods: {
    onLabelclick(index: number) {
      const widgetInfo = new WidgetInfo();
      widgetInfo.id = this.id;
      widgetInfo.label = this.labels[index];

      this.$emit("widgetAction", widgetInfo);
    }
  }
});
</script>

<style scoped>
.doughnutHeaderTitle {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.01em;
  color: #010606;
  margin-bottom: 16px;
}

.chart-area {
  display: flex;
  flex: 0 0 auto;
  min-height: 0;
  height: 160px;
  align-items: center;
}

.chart-container {
  flex: 0 0 auto;
  width: 160px;
  height: 160px;
  position: relative;
}

.custom-legend {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1 1 0;
  min-width: 120px;
  margin-left: 24px;
  overflow: hidden;
}

.legend-item {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  margin-bottom: 10px;
  gap: 8px;
  width: 100%;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: 0 0 10px;
  margin-top: 2px;
}

.legend-text {
  flex: 1 1 0;
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--v-theme-secondary20));
  white-space: normal;
  overflow-wrap: break-word;
  word-break: normal;
  min-width: 0;
  max-width: 100px;
}

.legend-value {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 400;
  color: rgb(var(--v-theme-secondary50));
  padding-left: 8px;
}

.legend-arrow {
  flex: 0 0 auto;
  font-size: 16px;
  opacity: 0.6;
  color: rgb(var(--v-theme-secondary40));
  padding-right: 8px;
}

.legend-item:hover .legend-arrow {
  opacity: 1;
}

.no-data-container {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  color: rgb(var(--v-theme-secondary50));
  text-align: left;
  min-height: 200px;
}
</style>
